import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment.dev';

/**
 * Reasons a widget can fail to provide a captcha token.
 */
export type TurnstileFailure = 'script' | 'render' | 'error' | 'timeout';

/**
 * Error raised by `TurnstileService` when no token could be obtained.
 */
export class TurnstileError extends Error {
	constructor(public readonly reason: TurnstileFailure, detail?: string) {
		super(detail ? `Turnstile ${reason}: ${detail}` : `Turnstile ${reason}`);
		this.name = 'TurnstileError';
	}
}

/**
 * Options accepted when rendering a widget.
 */
export interface TurnstileRenderOptions {
	/** Language of the widget as an ISO 639-1 code; the browser language when omitted. */
	language?: string;
	/** Notified whenever the widget gains a token (`string`) or loses it (`null`). */
	onTokenChange?: (token: string | null) => void;
}

/**
 * Bookkeeping for one rendered widget: its current token, the listener that
 * follows token changes and the waiter of a `getToken` call still pending.
 */
interface WidgetHandle {
	token?: string;
	onTokenChange?: (token: string | null) => void;
	pending?: {
		resolve: (token: string) => void;
		reject: (error: TurnstileError) => void;
		timer: number;
	};
}

/**
 * Wraps the Cloudflare Turnstile client API for the lead forms.
 *
 * Loads the Turnstile script once, on first use, and renders managed widgets:
 * the widget is visible inside the form, verifies the visitor as soon as it
 * renders and only asks for an interaction when the visitor looks suspicious.
 * Tokens are single-use, so a widget is reset after every submission and
 * verifies again on its own. Every widget is owned by the component that
 * rendered it; the service keeps the script loading promise and the token
 * state of each widget.
 */
@Injectable({
	providedIn: 'root',
})
export class TurnstileService {
	private static readonly SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
	private static readonly ONLOAD_CALLBACK = 'onTurnstileLoad';
	private static readonly TOKEN_WAIT_MS = 10000;

	private readonly siteKey = environment.turnstileSiteKey;
	private scriptLoad?: Promise<Turnstile.Api>;
	private readonly widgets = new Map<string, WidgetHandle>();

	/**
	 * Resolves the Turnstile API, injecting the script tag the first time it is
	 * needed. Subsequent calls reuse the same promise, and an already present
	 * `window.turnstile` resolves immediately.
	 */
	public load(): Promise<Turnstile.Api> {
		if (window.turnstile) {
			return Promise.resolve(window.turnstile);
		}
		if (!this.scriptLoad) {
			this.scriptLoad = new Promise<Turnstile.Api>((resolve, reject) => {
				window[TurnstileService.ONLOAD_CALLBACK] = () => {
					delete window[TurnstileService.ONLOAD_CALLBACK];
					if (window.turnstile) {
						resolve(window.turnstile);
					} else {
						reject(new TurnstileError('script', 'onload fired without turnstile'));
					}
				};
				const script = document.createElement('script');
				script.src = `${TurnstileService.SCRIPT_URL}?render=explicit&onload=${TurnstileService.ONLOAD_CALLBACK}`;
				script.async = true;
				script.defer = true;
				script.onerror = () => {
					this.scriptLoad = undefined;
					reject(new TurnstileError('script', 'script failed to load'));
				};
				document.head.appendChild(script);
			});
		}
		return this.scriptLoad;
	}

	/**
	 * Renders a managed widget inside the given container, in the site's dark
	 * theme and the requested language, and returns its widget id. The widget
	 * starts verifying immediately and reports its token through
	 * `onTokenChange`. Rejects with a `TurnstileError` when the script cannot be
	 * loaded or Cloudflare refuses to render (for example, a hostname that is
	 * not authorized for the site key).
	 */
	public async render(container: HTMLElement, options: TurnstileRenderOptions = {}): Promise<string> {
		const api = await this.load();
		const handle: WidgetHandle = { onTokenChange: options.onTokenChange };
		let widgetId: string | undefined;
		widgetId = api.render(container, {
			sitekey: this.siteKey,
			theme: 'dark',
			size: 'normal',
			language: options.language ? options.language.toLowerCase() : 'auto',
			action: 'lead-register',
			'refresh-expired': 'auto',
			callback: (token) => this.setToken(widgetId, token),
			'error-callback': (code) => {
				this.setToken(widgetId, undefined, new TurnstileError('error', code));
				return true;
			},
			'expired-callback': () => this.setToken(widgetId, undefined),
			'timeout-callback': () => this.setToken(widgetId, undefined, new TurnstileError('timeout', 'challenge')),
		});
		if (!widgetId) {
			throw new TurnstileError('render', 'render returned no widget id');
		}
		this.widgets.set(widgetId, handle);
		return widgetId;
	}

	/**
	 * Returns the current token of a widget, or waits for the widget to finish
	 * verifying when it has none yet. Rejects with a `TurnstileError` if the
	 * widget reports an error or does not deliver a token within the wait
	 * window. A widget serves one waiter at a time; a new call replaces a
	 * previous pending one.
	 */
	public getToken(widgetId: string): Promise<string> {
		const handle = this.widgets.get(widgetId);
		if (!handle) {
			return Promise.reject(new TurnstileError('render', 'unknown widget'));
		}
		if (handle.token) {
			return Promise.resolve(handle.token);
		}
		if (handle.pending) {
			this.settle(handle, undefined, new TurnstileError('error', 'superseded'));
		}
		return new Promise<string>((resolve, reject) => {
			const timer = window.setTimeout(
				() => this.settle(handle, undefined, new TurnstileError('timeout', 'no token')),
				TurnstileService.TOKEN_WAIT_MS,
			);
			handle.pending = { resolve, reject, timer };
		});
	}

	/**
	 * Resets a widget after its token has been used, so it verifies again and
	 * produces a new one. Any pending waiter is rejected.
	 */
	public reset(widgetId: string): void {
		this.setToken(widgetId, undefined, new TurnstileError('error', 'reset'));
		window.turnstile?.reset(widgetId);
	}

	/**
	 * Removes a widget from the page and forgets it, rejecting any pending waiter.
	 */
	public remove(widgetId: string): void {
		this.setToken(widgetId, undefined, new TurnstileError('error', 'removed'));
		window.turnstile?.remove(widgetId);
		this.widgets.delete(widgetId);
	}

	/**
	 * Records the token state of a widget, notifies its listener and completes
	 * a pending `getToken` waiter with the token or with the given error.
	 */
	private setToken(widgetId: string | undefined, token?: string, error?: TurnstileError): void {
		const handle = widgetId ? this.widgets.get(widgetId) : undefined;
		if (!handle) {
			return;
		}
		handle.token = token;
		handle.onTokenChange?.(token ?? null);
		if (token || error) {
			this.settle(handle, token, error);
		}
	}

	/**
	 * Completes the pending waiter of a widget, clearing its timeout. Widgets
	 * without a pending waiter are ignored.
	 */
	private settle(handle: WidgetHandle, token?: string, error?: TurnstileError): void {
		if (!handle.pending) {
			return;
		}
		const { resolve, reject, timer } = handle.pending;
		window.clearTimeout(timer);
		handle.pending = undefined;
		if (token) {
			resolve(token);
		} else {
			reject(error ?? new TurnstileError('error'));
		}
	}
}
